import { type AccentHue, accentHues } from "@repo/fika-design-system/color"
import { mergeAttributes, Node } from "@tiptap/core"

export const DEFAULT_CALLOUT_HUE: AccentHue = "sand"
export const DEFAULT_CALLOUT_EMOJI = "💡"

export const CALLOUT_NODE = "callout"
export const CALLOUT_CONTENT_NODE = "calloutContent"
export const CALLOUT_EMOJI_NODE = "calloutEmoji"

interface CalloutOptions {
  HTMLAttributes: Record<string, any>
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    callout: {
      setCallout: () => ReturnType
      toggleCallout: () => ReturnType
      unsetCallout: () => ReturnType
    }
  }
}

function parseHue(value: string | null): AccentHue {
  if (!value) return DEFAULT_CALLOUT_HUE
  return accentHues.includes(value as AccentHue)
    ? (value as AccentHue)
    : DEFAULT_CALLOUT_HUE
}

export const Callout = Node.create<CalloutOptions>({
  name: CALLOUT_NODE,

  addOptions() {
    return {
      HTMLAttributes: {},
    }
  },

  content: "block+",
  group: "block",
  defining: true,
  draggable: true,
  selectable: true,
  atom: true,

  addAttributes() {
    return {
      hue: {
        default: DEFAULT_CALLOUT_HUE,
        parseHTML: element => parseHue(element.getAttribute("data-hue")),
        renderHTML: attributes => ({
          "data-hue": parseHue(attributes.hue),
        }),
      },
      emoji: {
        default: DEFAULT_CALLOUT_EMOJI,
        parseHTML: element =>
          element.getAttribute("data-emoji") ?? DEFAULT_CALLOUT_EMOJI,
        renderHTML: attributes => ({
          "data-emoji":
            typeof attributes.emoji === "string" && attributes.emoji.length > 0
              ? attributes.emoji
              : DEFAULT_CALLOUT_EMOJI,
        }),
      },
      uid: {
        default: null,
        parseHTML: element => element.getAttribute("data-uid"),
        renderHTML: attributes =>
          attributes.uid ? { "data-uid": attributes.uid } : {},
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: `aside[data-type="${CALLOUT_NODE}"]`,
        contentElement: node =>
          node.querySelector(`div[data-subtype="${CALLOUT_CONTENT_NODE}"]`) ??
          node,
      },
    ]
  },

  renderHTML({ HTMLAttributes, node }) {
    const hue = parseHue(node.attrs.hue as string | null)
    const emoji =
      typeof node.attrs.emoji === "string" && node.attrs.emoji.length > 0
        ? node.attrs.emoji
        : DEFAULT_CALLOUT_EMOJI

    return [
      "aside",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        "data-type": CALLOUT_NODE,
        "data-hue": hue,
        "data-emoji": emoji,
      }),
      [
        "button",
        {
          "data-subtype": CALLOUT_EMOJI_NODE,
          "data-callout-emoji-trigger": "true",
          type: "button",
        },
        emoji,
      ],
      [
        "div",
        {
          "data-subtype": CALLOUT_CONTENT_NODE,
        },
        0,
      ],
    ]
  },

  addCommands() {
    return {
      setCallout:
        () =>
        ({ commands }) => {
          return commands.wrapIn(this.name)
        },
      toggleCallout:
        () =>
        ({ commands }) => {
          return commands.toggleWrap(this.name)
        },
      unsetCallout:
        () =>
        ({ commands }) => {
          return commands.lift(this.name)
        },
    }
  },
})
